import './styles/tokens.css'
import './styles/layout.css'
import './styles/components.css'

import mermaid from 'mermaid'
import { sections, type Section } from './content/sections'
import { diagrams } from './content/diagrams'
import { renderNav, initNav } from './components/nav'
import { renderHero } from './components/hero'
import { renderSurfaceTabs, initSurfaceTabs } from './components/surface-tabs'
import { renderFlowExplorer, initFlowExplorer } from './components/flow-explorer'
import { renderCacheLayers, initCacheLayers } from './components/cache-layers'
import { renderGlossary } from './components/glossary'

const renderedDiagrams = new Set<string>()

/**
 * Initializes Mermaid with theme-aware styling.
 */
function initMermaid(): void {
  const isDark = window.matchMedia('(prefers-color-scheme: dark)').matches

  mermaid.initialize({
    startOnLoad: false,
    theme: 'base',
    themeVariables: {
      primaryColor: '#fff0ec',
      primaryTextColor: '#363636',
      primaryBorderColor: '#ff5733',
      lineColor: '#363636',
      secondaryColor: '#eeeeee',
      tertiaryColor: '#f5f5f5',
      mainBkg: '#ffffff',
      nodeBorder: '#ff5733',
      clusterBkg: '#f5f5f5',
      titleColor: '#363636',
      edgeLabelBackground: '#ffffff',
      ...(isDark
        ? {
            primaryColor: '#3d2a24',
            primaryTextColor: '#f0f0f0',
            primaryBorderColor: '#ff5733',
            lineColor: '#c8c8c8',
            secondaryColor: '#424242',
            tertiaryColor: '#363636',
            mainBkg: '#363636',
            nodeBorder: '#ff5733',
            clusterBkg: '#2a2a2a',
            titleColor: '#f0f0f0',
            edgeLabelBackground: '#363636',
          }
        : {}),
    },
    securityLevel: 'strict',
    flowchart: { curve: 'basis', padding: 16 },
    sequence: { actorMargin: 40, messageMargin: 30 },
  })
}

/**
 * Renders a Mermaid diagram into a container by id.
 */
async function renderDiagram(diagramId: string, container: HTMLElement): Promise<void> {
  if (renderedDiagrams.has(diagramId)) return

  const source = diagrams[diagramId]
  if (!source) return

  const uid = `mermaid-${diagramId}-${Date.now()}`
  try {
    const { svg } = await mermaid.render(uid, source)
    container.innerHTML = svg
    renderedDiagrams.add(diagramId)
  } catch (err) {
    container.innerHTML = `<p style="color:var(--color-text-muted)">Diagram could not be rendered.</p>`
    console.error(`Mermaid render failed for ${diagramId}:`, err)
  }
}

/**
 * Returns interactive widget HTML for a section.
 */
function renderInteractive(type: Section['interactive']): string {
  switch (type) {
    case 'surfaces':
      return renderSurfaceTabs()
    case 'flow':
      return renderFlowExplorer()
    case 'cache':
      return renderCacheLayers()
    case 'glossary':
      return renderGlossary()
    default:
      return ''
  }
}

/**
 * Renders a single documentation section.
 */
function renderSection(section: Section, index: number): string {
  const diagramHtml = section.diagramId
    ? `<div class="diagram-wrap" data-diagram="${section.diagramId}" aria-label="Architecture diagram"></div>`
    : ''

  const interactiveHtml = section.interactive ? renderInteractive(section.interactive) : ''

  const callout =
    section.id === 'resilience'
      ? `<div class="callout callout--warning">
          <strong>Emergency rollback</strong>
          Set <code>ENABLE_UNBXD_SEARCH=false</code> and restart the storefront. All search traffic returns to VTEX Intelligent Search — no rebuild required.
        </div>`
      : ''

  return `<section class="section" id="${section.id}" aria-labelledby="title-${section.id}">
    <header class="section__header">
      <span class="section__number">Section ${String(index + 1).padStart(2, '0')}</span>
      <h2 class="section__title" id="title-${section.id}">${section.title}</h2>
    </header>
    <p class="section__executive">${section.executive}</p>
    ${callout}
    ${interactiveHtml}
    ${diagramHtml}
    <div class="accordion" data-accordion>
      <button type="button" class="accordion__trigger" aria-expanded="false">
        Explore technical details
        <span class="accordion__icon" aria-hidden="true">+</span>
      </button>
      <div class="accordion__panel">${section.details}</div>
    </div>
  </section>`
}

/**
 * Wires accordion expand/collapse behavior.
 */
function initAccordions(): void {
  document.querySelectorAll('[data-accordion]').forEach((accordion) => {
    const trigger = accordion.querySelector<HTMLButtonElement>('.accordion__trigger')
    if (!trigger) return

    trigger.addEventListener('click', () => {
      const isOpen = accordion.classList.toggle('is-open')
      trigger.setAttribute('aria-expanded', String(isOpen))
    })
  })
}

/**
 * Lazy-renders Mermaid diagrams when sections enter the viewport.
 */
function initDiagramObserver(): void {
  const containers = document.querySelectorAll<HTMLElement>('[data-diagram]')
  if (!containers.length) return

  const observer = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          const el = entry.target as HTMLElement
          const diagramId = el.dataset.diagram
          if (diagramId) {
            renderDiagram(diagramId, el)
            observer.unobserve(el)
          }
        }
      })
    },
    { rootMargin: '100px' }
  )

  containers.forEach((el) => observer.observe(el))
}

/**
 * Boots the documentation site.
 */
function boot(): void {
  initMermaid()

  const sectionsHtml = sections.map((s, i) => renderSection(s, i)).join('')

  const app = document.getElementById('app')
  if (!app) return

  app.innerHTML = `
    <header class="site-header">
      <a href="#" class="site-header__brand">
        <img src="/favicon.svg" alt="" class="site-header__logo" width="36" height="36" />
        <span>
          <span class="site-header__title">Unbxd × FastStore</span>
          <span class="site-header__subtitle">Architecture Guide</span>
        </span>
      </a>
      <button type="button" class="nav-toggle" id="nav-toggle" aria-expanded="false" aria-controls="sidebar">
        Menu
      </button>
    </header>
    <div class="sidebar-overlay" id="sidebar-overlay"></div>
    <div class="layout">
      ${renderNav()}
      <main class="main">
        ${renderHero()}
        ${sectionsHtml}
        <footer class="site-footer">
          <p>Worldwide Golf · Unbxd × FastStore Architecture Documentation</p>
        </footer>
      </main>
    </div>
  `

  initNav()
  initAccordions()
  initSurfaceTabs()
  initFlowExplorer()
  initCacheLayers()
  initDiagramObserver()
}

boot()
